import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import '../../style/speakSearch.css';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const BulkSlipPrintApp = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // --- Get App ID from Query String ---
    const queryParams = new URLSearchParams(location.search);
    const initialAppId = queryParams.get('app_id') || "";
    
    const lang = 'en';

    // --- State ---
    const [currentAppId, setCurrentAppId] = useState(initialAppId);
    const [vidhanSabhaList, setVidhanSabhaList] = useState([]); 
    const [booths, setBooths] = useState([]);
    const [selectedBooth, setSelectedBooth] = useState("");
    const [voters, setVoters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchingBooths, setFetchingBooths] = useState(false);
    const [showActionButtons, setShowActionButtons] = useState(false);
    const [isSamplePrinting, setIsSamplePrinting] = useState(false);
    const [sampleVoters, setSampleVoters] = useState([]);
    
    // --- Image Upload State ---
    const [uploadedImage, setUploadedImage] = useState(null);
    
    // --- Dropdown Search States ---
    const [vidhanSabhaSearch, setVidhanSabhaSearch] = useState("");
    const [boothSearch, setBoothSearch] = useState("");
    const [isVidhanSabhaOpen, setIsVidhanSabhaOpen] = useState(false);
    const [isBoothOpen, setIsBoothOpen] = useState(false);
    
    const printRef = useRef();
    const vidhanSabhaRef = useRef();
    const boothRef = useRef();

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Bulk Slip Print",
            selectVidhanSabha: "Select Vidhan Sabha",
            selectBooth: "Select Booth Number",
            btnGenerate: "Generate All Slips",  
            btnExportPDF: "Print / Export PDF",
            loadingBooths: "Loading Booths...",
            loadingData: "Fetching Voters...",
            noVoters: "No voters found for this booth.",
            btnSample: "Sample View (Print 10 Slips)",
            uploadBanner: "Upload a slip image",
            filterEyebrow: "Slip Generator",
            labelVidhanSabha: "Vidhan Sabha",
            labelBooth: "Booth No.",
            labelUpload: "Slip Banner",
            searchPlaceholder: "Search..."
        },
        hi: {
            pageTitle: "बल्क स्लिप प्रिंट",
            selectVidhanSabha: "विधानसभा चुनें",
            selectBooth: "बूथ नंबर चुनें",
            btnGenerate: "सभी स्लिप बनाएं",
            btnExportPDF: "प्रिंट / पीडीएफ डाउनलोड करें",
            loadingBooths: "बूथ लोड हो रहे हैं...",
            loadingData: "मतदाता लाए जा रहे हैं...",
            noVoters: "इस बूथ के लिए कोई मतदाता नहीं मिला।",
            btnSample: "नमूना देखें (10 पर्ची प्रिंट करें)",
            uploadBanner: "स्लिप इमेज सिलेक्ट करें",
            filterEyebrow: "स्लिप जनरेटर",
            labelVidhanSabha: "विधानसभा",
            labelBooth: "बूथ नंबर",
            labelUpload: "स्लिप बैनर",
            searchPlaceholder: "खोजें..."
        },
        gu: {
            pageTitle: "બલ્ક સ્લિપ પ્રિન્ટ",
            selectVidhanSabha: "વિધાનસભા પસંદ કરો",
            selectBooth: "બૂથ નંબર પસંદ કરો",
            btnGenerate: "બધા સ્લિપ બનાવો",
            btnExportPDF: "પ્રિન્ટ / PDF ડાઉનલોડ કરો",
            loadingBooths: "બૂથ લોડ થઈ રહ્યા છે...",
            loadingData: "મતદારો લાવવામાં આવી રહ્યા છે...",
            noVoters: "આ બૂથ માટે કોઈ મતદાર મળ્યો નથી.",
            btnSample: "સેમ્પલ જુઓ (૧૦ સ્લિપ પ્રિન્ટ કરો)",
            uploadBanner: "સ્લિપ ઇમેજ સિલેક્ટ કરો",
            filterEyebrow: "સ્લિપ જનરેટર",
            labelVidhanSabha: "વિધાનસભા",
            labelBooth: "બૂથ નંબર",
            labelUpload: "સ્લિપ બેનર",
            searchPlaceholder: "શોધો..."
        }
    };

    const txt = t[lang] || t.en;

    // --- Fetch Vidhan Sabha and Booths Data on Load ---
    useEffect(() => {
        const fetchVidhanSabhas = async () => {
            setFetchingBooths(true);
            try {
                const response = await axios.post(`${BASE_URL}/api/dis_vidhansabha_booth_voter`, {
                    app_id: "" // Passing blank app_id to get all data
                });
                
                if (response.data.success && response.data.data && response.data.data.result) {
                    setVidhanSabhaList(response.data.data.result);
                } else {
                    setVidhanSabhaList([]);
                }
            } catch (error) {
                console.error("Error fetching vidhan sabhas:", error);
                setVidhanSabhaList([]);
            } finally {
                setFetchingBooths(false);
            }
        };

        fetchVidhanSabhas();
    }, []);

    // --- Update Booths whenever currentAppId or vidhanSabhaList changes ---
    useEffect(() => {
        if (!currentAppId) {
            setBooths([]);
            setSelectedBooth("");
            setShowActionButtons(false);
            setVoters([]);
            return;
        }

        const selectedVS = vidhanSabhaList.find(vs => vs.app_id === currentAppId);
        if (selectedVS && selectedVS.booth) {
            setBooths(selectedVS.booth);
        } else {
            setBooths([]);
        }
        
        setSelectedBooth("");
        setVoters([]);
        setShowActionButtons(false);
        setBoothSearch("");
    }, [currentAppId, vidhanSabhaList]);

    // --- Handle Vidhan Sabha Change ---
    const handleVidhanSabhaSelect = (appId) => {
        setCurrentAppId(appId);
        setIsVidhanSabhaOpen(false);
        setVidhanSabhaSearch("");
    };

    // --- Handle Image Upload ---
    const handleImageUpload = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const imageUrl = URL.createObjectURL(file);
            setUploadedImage(imageUrl);
        }
    };

    // --- Generate Full Voter List ---
    const handleGenerate = async () => {
        if (!currentAppId) {
            alert(txt.selectVidhanSabha);
            return;
        }
        if (!selectedBooth) {
            alert(txt.selectBooth);
            return;
        }

        setLoading(true);
        setShowActionButtons(false);

        try {
            const response = await axios.post(`${BASE_URL}/api/dis_booth_wise_search`, {
                app_id: currentAppId,
                booth_no: selectedBooth
            });

            if (response.data.success) {
                const fetchedVoters = response.data.data.result || [];
                setVoters(fetchedVoters);
                setShowActionButtons(true);
            } else {
                setVoters([]);
                setShowActionButtons(false);
            }
        } catch (error) {
            console.error("Error fetching voters:", error);
            setVoters([]);
            setShowActionButtons(false);
        } finally {
            setLoading(false);
        }
    };

    // --- Handle Sample Print (Only First 10 Slips) - Does NOT change main display ---
    const handleSamplePrint = async () => {
        if (!currentAppId || !selectedBooth) return;
        
        setIsSamplePrinting(true);
        try {
            const response = await axios.post(`${BASE_URL}/api/dis_booth_wise_search`, {
                app_id: currentAppId,
                booth_no: selectedBooth
            });

            if (response.data.success) {
                const allVoters = response.data.data.result || [];
                const sampleData = allVoters.slice(0, 10);
                setSampleVoters(sampleData);
                
                setTimeout(() => {
                    window.print();
                }, 300);
            }
        } catch (error) {
            console.error("Error fetching voters for sample:", error);
            alert("Failed to load sample data");
        } finally {
            // Clear sample voters after print dialog closes (with a delay)
            setTimeout(() => {
                setIsSamplePrinting(false);
                setSampleVoters([]);
            }, 1000);
        }
    };
    
    // --- Handle PDF Export / Print (Full List) ---
    const handleExportPDF = () => {
        window.print();
    };

    // --- Click outside handler for dropdowns ---
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (vidhanSabhaRef.current && !vidhanSabhaRef.current.contains(event.target)) {
                setIsVidhanSabhaOpen(false);
            }
            if (boothRef.current && !boothRef.current.contains(event.target)) {
                setIsBoothOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const isGenerateDisabled = loading || !selectedBooth || !currentAppId;

    // Determine which voters to display (main list or sample for print)
    const displayVoters = isSamplePrinting ? sampleVoters : voters;
    const shouldShowPrintArea = (!loading && displayVoters.length > 0) || isSamplePrinting;

    // --- Filtered lists ---
    const filteredVidhanSabha = vidhanSabhaList.filter(vs => 
        vs.vidhansabha_name?.toLowerCase().includes(vidhanSabhaSearch.toLowerCase()) ||
        vs.vidhansabha_no?.toString().includes(vidhanSabhaSearch)
    );

    const filteredBooths = booths.filter(b => 
        b.booth_no?.toString().includes(boothSearch)
    );

    return (
        <div className='app-container p-4'>
            <div className="no-print">
                
                {/* --- FILTER BAR --- */}
                <div className="controls-row mb-4 slide-up-element" style={{ animationDelay: '0.1s' , position: 'sticky', top: '16px', zIndex: 10 }}>
                    <div className="filter-bar-eyebrow">
                        <span className="filter-bar-eyebrow-dot"></span>
                        {txt.filterEyebrow}
                    </div>
                    <div className="d-flex flex-wrap gap-3 align-items-end filter-fields-row">
                        
                        {/* 1. Vidhan Sabha Dropdown with Search */}
                        <div className="filter-field flex-grow-1" style={{ maxWidth: '260px' }} ref={vidhanSabhaRef}>
                            <label className="filter-field-label">{txt.labelVidhanSabha}</label>
                            <div className="search-wrapper-modern dropdown-wrapper">
                                <svg className="search-icon-modern" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                <div 
                                    className="search-input-modern dropdown-input"
                                    onClick={() => !fetchingBooths && setIsVidhanSabhaOpen(!isVidhanSabhaOpen)}
                                    style={{ cursor: fetchingBooths ? 'not-allowed' : 'pointer' }}
                                >
                                    {currentAppId ? 
                                        vidhanSabhaList.find(vs => vs.app_id === currentAppId)?.vidhansabha_name || txt.selectVidhanSabha 
                                        : txt.selectVidhanSabha
                                    }
                                </div>
                                <svg className="select-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
                                </svg>
                                
                                {isVidhanSabhaOpen && !fetchingBooths && (
                                    <div className="dropdown-menu-modern">
                                        <div className="dropdown-search-wrapper">
                                            <svg className="dropdown-search-icon" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                            <input
                                                type="text"
                                                className="dropdown-search-input"
                                                placeholder={txt.searchPlaceholder}
                                                value={vidhanSabhaSearch}
                                                onChange={(e) => setVidhanSabhaSearch(e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                        <div className="dropdown-items">
                                            {filteredVidhanSabha.length > 0 ? (
                                                filteredVidhanSabha.map(vs => (
                                                    <div 
                                                        key={vs.app_id} 
                                                        className={`dropdown-item ${currentAppId === vs.app_id ? 'active' : ''}`}
                                                        onClick={() => handleVidhanSabhaSelect(vs.app_id)}
                                                    >
                                                        <span className="dropdown-item-text">{vs.vidhansabha_no} - {vs.vidhansabha_name}</span>
                                                        {currentAppId === vs.app_id && (
                                                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="dropdown-empty">No results found</div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* 2. Booth Number Dropdown with Search */}
                        <div className="filter-field flex-grow-1 filter-field-booth" style={{ maxWidth: '260px' }} ref={boothRef}>
                            <label className="filter-field-label">{txt.labelBooth}</label>
                            <div className="search-wrapper-modern dropdown-wrapper">
                                <svg className="search-icon-modern" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <div 
                                    className="search-input-modern dropdown-input"
                                    onClick={() => currentAppId && !fetchingBooths && setIsBoothOpen(!isBoothOpen)}
                                    style={{ cursor: currentAppId && !fetchingBooths ? 'pointer' : 'not-allowed' }}
                                >
                                    {selectedBooth ? `Booth No: ${selectedBooth}` : txt.selectBooth}
                                </div>
                                <svg className="select-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
                                </svg>
                                
                                {isBoothOpen && currentAppId && !fetchingBooths && (
                                    <div className="dropdown-menu-modern">
                                        <div className="dropdown-search-wrapper">
                                            <svg className="dropdown-search-icon" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                            <input
                                                type="text"
                                                className="dropdown-search-input"
                                                placeholder={txt.searchPlaceholder}
                                                value={boothSearch}
                                                onChange={(e) => setBoothSearch(e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                        <div className="dropdown-items">
                                            {filteredBooths.length > 0 ? (
                                                filteredBooths.map((b, i) => (
                                                    <div 
                                                        key={i} 
                                                        className={`dropdown-item ${selectedBooth === b.booth_no ? 'active' : ''}`}
                                                        onClick={() => {
                                                            setSelectedBooth(b.booth_no);
                                                            setShowActionButtons(false);
                                                            setVoters([]);
                                                            setIsBoothOpen(false);
                                                            setBoothSearch("");
                                                        }}
                                                    >
                                                        <span className="dropdown-item-text">
                                                            Booth No: {b.booth_no} {b.total_voter ? `(${b.total_voter} Voters)` : ''}
                                                        </span>
                                                        {selectedBooth === b.booth_no && (
                                                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="dropdown-empty">No results found</div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* 3. Upload Image Input (Appears only after Booth is selected) */}
                        {currentAppId && selectedBooth && (
                            <div className="filter-field flex-grow-1" style={{ maxWidth: '260px' }}>
                                <label className="filter-field-label">{txt.labelUpload}</label>
                                <label 
                                    className={`upload-control-modern w-100 d-flex align-items-center m-0 ${uploadedImage ? 'is-filled' : ''}`}
                                >
                                    <span className="upload-control-text">
                                        {uploadedImage ? (
                                            <>
                                                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                </svg>
                                                Image Selected
                                            </>
                                        ) : (
                                            <>
                                                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                                </svg>
                                                {txt.uploadBanner}
                                            </>
                                        )}
                                    </span>
                                    <input 
                                        type="file" 
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        style={{ display: 'none' }} 
                                    />
                                </label>
                            </div>
                        )}

                        {/* Generate All Slips Button */}
                        <button 
                            className="btn-modern btn-generate-modern" 
                            onClick={handleGenerate}
                            disabled={isGenerateDisabled}
                        >
                            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                            <span>{txt.btnGenerate}</span>
                        </button>

                        {/* Action Buttons - Show only after Generate is clicked */}
                        {showActionButtons && voters.length > 0 && (
                            <>
                                {/* Sample View Button */}
                                <button 
                                    className="btn-modern btn-sample-modern"
                                    onClick={handleSamplePrint}
                                    disabled={loading || isSamplePrinting}
                                >
                                    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                    <span>{txt.btnSample}</span>
                                </button>

                                {/* Export PDF Button */}
                                <button 
                                    className="btn-modern btn-export-modern ms-auto"
                                    onClick={handleExportPDF}
                                >
                                    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                                    <span>{txt.btnExportPDF}</span>
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {loading && (
                <div className="loader-wrapper no-print">
                    <div className="spinner"></div>
                    <p className="loader-text">{txt.loadingData}</p>
                </div>
            )}

            {!selectedBooth && !loading && !showActionButtons && (
                <div className="text-center text-muted p-5 modern-card mt-3 no-print">
                    <h4>{txt.selectBooth}</h4>
                </div>
            )}

            {selectedBooth && !loading && voters.length === 0 && showActionButtons && !isSamplePrinting && (
                <div className="text-center text-muted p-5 modern-card mt-3 no-print">
                    <h4>{txt.noVoters}</h4>
                </div>
            )}

            {/* --- PRINT AREA - Shows either full list OR sample list during printing --- */}
            <div id="printable-a4-area" className="screen-preview">
                {shouldShowPrintArea && (
                    <div className="continuous-a4-sheet">
                        <div className="slip-grid-dynamic">
                            {displayVoters.map((voter, index) => (
                                <div className="voter-card-print" key={`${voter.id || index}-${isSamplePrinting ? 'sample' : 'full'}`}>
                                    <div className="slip-container" style={{ border: '2px solid #000', backgroundColor: '#fff', color: '#000', height: "fit-content", display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
                                        <div style={{ flex: 'none', width: '100%', borderBottom: '2px solid #000', backgroundColor: '#fff', display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}>
                                            <img 
                                                src={uploadedImage || "http://rajkotcorporation.bjpwings.com/img/prachar/19042026030352_1.jpg"} 
                                                alt="Slip Banner" 
                                                style={{ width: '100%', height: 'auto', display: 'block' }} 
                                            />
                                        </div>
                                        <div style={{ flex: 'none', padding: '8px', fontSize: '13px', lineHeight: '1.6', fontWeight: '600' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', flexWrap: 'wrap' }}>
                                                <span>ભાગ નં - {voter.part_no || "-"}</span>
                                                <span>બૂથ નં - {voter.booth_no || voter.part_no || "-"}</span>
                                                <span>ક્રમ નં. - {voter.serial_no || voter.slnoinpart || (index + 1)}</span>
                                            </div>
                                            <div style={{ marginBottom: '3px' }}>નામ : {voter.eng_f_name} {voter.eng_surname}</div>
                                            <div style={{ marginBottom: '3px' }}>જાતિ/ઉંમર : {voter.sex_age || voter.sex || "-"}</div>
                                            <div style={{ marginBottom: '3px' }}>આઈડીકાર્ડ : {voter.idcard_no || "-"}</div>
                                            <div>મતદાન સ્થળ : {voter.eng_polling_location || "-"}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <style jsx>{`
                .app-container { background: #E5E7EB; min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; padding-bottom: 80px; }
                
                /* --- MODERNIZED FILTER BAR --- */
                .controls-row {
                    background: rgba(255, 255, 255, 0.9);
                    backdrop-filter: blur(14px);
                    -webkit-backdrop-filter: blur(14px);
                    padding: 20px 24px 22px;
                    border-radius: 20px;
                    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 12px 32px -12px rgba(15, 23, 42, 0.14);
                    border: 1px solid rgba(226, 232, 240, 0.9);
                    margin-top: 10px;
                    position: relative;
                    overflow: visible !important;
                    animation: filterBarEnter 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
                    transition: box-shadow 0.25s ease, transform 0.25s ease;
                }
                .controls-row:hover {
                    box-shadow: 0 2px 4px rgba(15, 23, 42, 0.05), 0 18px 40px -14px rgba(67, 56, 202, 0.22);
                }
                .controls-row::before {
                    content: "";
                    position: absolute;
                    top: 0; left: 0; right: 0;
                    height: 3px;
                    background: linear-gradient(90deg, #4338CA, #6366F1, #0EA5E9, #6366F1, #4338CA);
                    background-size: 200% 100%;
                    animation: shimmerBar 5s linear infinite;
                    border-radius: 20px 20px 0 0;
                }

                @keyframes filterBarEnter {
                    from { opacity: 0; transform: translateY(-10px) scale(0.99); }
                    to { opacity: 1; transform: translateY(0) scale(1); }
                }
                @keyframes shimmerBar {
                    0% { background-position: 0% 0%; }
                    100% { background-position: -200% 0%; }
                }
                @keyframes softPulse {
                    0%, 100% { box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15); }
                    50% { box-shadow: 0 0 0 6px rgba(99, 102, 241, 0.06); }
                }
                @keyframes popIn {
                    0% { transform: scale(0.85); opacity: 0; }
                    60% { transform: scale(1.06); opacity: 1; }
                    100% { transform: scale(1); }
                }
                @keyframes dropdownSlideIn {
                    from { opacity: 0; transform: translateY(-8px) scale(0.97); }
                    to { opacity: 1; transform: translateY(0) scale(1); }
                }

                .filter-bar-eyebrow {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 11px;
                    font-weight: 700;
                    letter-spacing: 0.09em;
                    text-transform: uppercase;
                    background: linear-gradient(90deg, #4338CA, #6366F1);
                    -webkit-background-clip: text;
                    background-clip: text;
                    color: transparent;
                    margin-bottom: 14px;
                }
                .filter-bar-eyebrow-dot {
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: #6366F1;
                    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
                    animation: softPulse 2.2s ease-in-out infinite;
                }

                .filter-fields-row {
                    row-gap: 16px !important;
                }

                .filter-field { display: flex; flex-direction: column; gap: 6px; position: relative; }
                .filter-field-label {
                    font-size: 11.5px;
                    font-weight: 700;
                    color: #64748B;
                    letter-spacing: 0.04em;
                    text-transform: uppercase;
                    margin: 0 0 1px 2px;
                }

                /* --- DROPDOWN WRAPPER --- */
                .dropdown-wrapper { position: relative; }

                .search-wrapper-modern { position: relative; }
                .search-icon-modern { 
                    position: absolute; 
                    left: 14px; 
                    top: 50%; 
                    transform: translateY(-50%); 
                    color: #94a3b8; 
                    width: 17px; 
                    height: 17px; 
                    pointer-events: none;
                    z-index: 2;
                }
                .select-chevron { 
                    position: absolute; 
                    right: 14px; 
                    top: 50%; 
                    transform: translateY(-50%); 
                    color: #94a3b8; 
                    pointer-events: none;
                    z-index: 2;
                }
                .search-input-modern {
                    width: 100%;
                    padding: 12px 40px 12px 42px;
                    border: 1.5px solid #E2E8F0;
                    border-radius: 12px;
                    font-size: 14.5px;
                    font-weight: 500;
                    color: #1e293b;
                    background-color: #F8FAFC;
                    outline: none;
                    transition: all 0.18s ease;
                    cursor: pointer;
                    appearance: none;
                    -webkit-appearance: none;
                    position: relative;
                    z-index: 1;
                }
                .search-input-modern:hover:not(:disabled) { 
                    border-color: #C7D2FE; 
                    background-color: #ffffff; 
                    transform: translateY(-1px); 
                }
                .search-input-modern:focus { 
                    border-color: #4338CA; 
                    box-shadow: 0 0 0 4px rgba(67, 56, 202, 0.12); 
                    background-color: white;
                }
                .search-input-modern:disabled { opacity: 0.6; cursor: not-allowed; }

                .dropdown-input {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .filter-field-booth .search-input-modern:focus { border-color: #0EA5E9; box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.14); }
                .filter-field-booth .search-icon-modern, .filter-field-booth .select-chevron { color: #0EA5E9; }

                /* --- DROPDOWN MENU --- */
                .dropdown-menu-modern {
                    position: absolute;
                    top: calc(100% + 6px);
                    left: 0;
                    right: 0;
                    background: #FFFFFF;
                    border-radius: 14px;
                    box-shadow: 0 12px 40px -8px rgba(15, 23, 42, 0.25), 0 4px 12px rgba(0, 0, 0, 0.06);
                    border: 1px solid #E2E8F0;
                    z-index: 1000;
                    animation: dropdownSlideIn 0.2s cubic-bezier(0.22, 1, 0.36, 1);
                    overflow: hidden;
                    min-width: 100%;
                    max-height: 320px;
                    display: flex;
                    flex-direction: column;
                }

                .dropdown-search-wrapper {
                    padding: 10px 12px;
                    border-bottom: 1px solid #F1F5F9;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    flex-shrink: 0;
                    background: #FAFBFC;
                }

                .dropdown-search-icon {
                    color: #94a3b8;
                    flex-shrink: 0;
                }

                .dropdown-search-input {
                    flex: 1;
                    border: none;
                    outline: none;
                    font-size: 14px;
                    color: #1e293b;
                    background: transparent;
                    padding: 6px 0;
                }
                .dropdown-search-input::placeholder {
                    color: #94a3b8;
                }

                .dropdown-items {
                    overflow-y: auto;
                    padding: 6px 0;
                    flex: 1;
                }

                .dropdown-items::-webkit-scrollbar {
                    width: 6px;
                }
                .dropdown-items::-webkit-scrollbar-track {
                    background: transparent;
                }
                .dropdown-items::-webkit-scrollbar-thumb {
                    background: #E2E8F0;
                    border-radius: 3px;
                }
                .dropdown-items::-webkit-scrollbar-thumb:hover {
                    background: #CBD5E1;
                }

                .dropdown-item {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 10px 16px;
                    cursor: pointer;
                    transition: all 0.12s ease;
                    font-size: 14px;
                    color: #1e293b;
                    font-weight: 500;
                    gap: 8px;
                }
                .dropdown-item:hover {
                    background: #F1F5F9;
                }
                .dropdown-item.active {
                    background: #EEF2FF;
                    color: #4338CA;
                }
                .dropdown-item .dropdown-item-text {
                    flex: 1;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .dropdown-item svg {
                    flex-shrink: 0;
                    color: #4338CA;
                }

                .dropdown-empty {
                    padding: 16px;
                    text-align: center;
                    color: #94a3b8;
                    font-size: 14px;
                    font-weight: 500;
                }

                .upload-control-modern {
                    padding: 12px 16px;
                    border-radius: 12px;
                    cursor: pointer;
                    background: #F8FAFC;
                    border: 1.5px dashed #CBD5E1;
                    color: #64748B;
                    transition: all 0.18s ease;
                }
                .upload-control-modern:hover { border-color: #4338CA; background: #F5F5FF; color: #4338CA; transform: translateY(-1px); }
                .upload-control-modern.is-filled {
                    background: #ECFDF5;
                    border: 1.5px solid #10B981;
                    color: #047857;
                    animation: popIn 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
                }
                .upload-control-text {
                    font-weight: 600;
                    font-size: 13.5px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .modern-card { background: #FFFFFF; border-radius: 16px; box-shadow: 0 4px 12px rgba(0,0,0,0.05); border: 1px solid #E2E8F0; }

                /* --- MODERN BUTTONS --- */
                .btn-modern {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    padding: 12px 22px;
                    border-radius: 12px;
                    font-weight: 600;
                    font-size: 14.5px;
                    color: #fff;
                    border: none;
                    cursor: pointer;
                    transition: transform 0.16s ease, box-shadow 0.16s ease, filter 0.16s ease;
                    white-space: nowrap;
                }
                .btn-modern:active:not(:disabled) { transform: translateY(0); filter: brightness(0.97); }
                .btn-modern:disabled { background: #CBD5E1 !important; cursor: not-allowed; box-shadow: none; }

                .btn-generate-modern { background: linear-gradient(135deg, #4338CA, #4F46E5); box-shadow: 0 6px 16px -6px rgba(67, 56, 202, 0.5); }
                .btn-generate-modern:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 10px 22px -6px rgba(67, 56, 202, 0.55); }

                .btn-sample-modern { background: linear-gradient(135deg, #059669, #10B981); box-shadow: 0 6px 16px -6px rgba(5, 150, 105, 0.5); }
                .btn-sample-modern:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 10px 22px -6px rgba(5, 150, 105, 0.55); }

                .btn-export-modern { background: linear-gradient(135deg, #EA580C, #F97316); box-shadow: 0 6px 16px -6px rgba(234, 88, 12, 0.5); }
                .btn-export-modern:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 10px 22px -6px rgba(234, 88, 12, 0.55); }

                /* --- DYNAMIC CONTINUOUS A4 SHEET DESIGN --- */
                .screen-preview {
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .continuous-a4-sheet {
                    width: 210mm;
                    padding: 10mm;
                    background: white;
                    box-shadow: 0 10px 25px rgba(0,0,0,0.1);
                }

                /* Using flex guarantees perfect row breaking */
                .slip-grid-dynamic {
                    display: flex; 
                    flex-wrap: wrap;
                    align-content: flex-start;
                    gap: 10mm;
                    width: 100%;
                }

                .voter-card-print {
                    width: calc(50% - 5mm);
                    height: fit-content;
                    page-break-inside: avoid;
                    break-inside: avoid;
                }

                /* Responsive Screen Scaling */
                @media screen and (max-width: 820px) {
                    .continuous-a4-sheet { transform: scale(0.8); transform-origin: top center; margin-bottom: -10%; }
                }
                @media screen and (max-width: 640px) {
                    .continuous-a4-sheet { transform: scale(0.6); transform-origin: top center; margin-bottom: -30%; }
                }
                @media screen and (max-width: 480px) {
                    .continuous-a4-sheet { transform: scale(0.43); transform-origin: top center; margin-bottom: -50%; }
                }
                @media screen and (max-width: 380px) {
                    .continuous-a4-sheet { transform: scale(0.36); transform-origin: top center; margin-bottom: -60%; }
                }

                /* --- STRICT PRINT STYLES --- */
                @media print {
                    @page {
                        size: A4 portrait;
                        margin: 10mm 0mm 0mm 0mm !important; 
                    }
                    body, html {
                        margin: 0 !important;
                        padding: 0 !important;
                    }
                    body * {
                        visibility: hidden;
                    }
                    .app-container {
                        padding: 0 !important;
                        margin: 0 !important;
                        background: transparent !important;
                    }
                    .screen-preview, .screen-preview * {
                        visibility: visible;
                    }
                    .screen-preview {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100% !important;
                        margin: 0 !important;
                        padding: 0 !important;
                    }
                    .continuous-a4-sheet {
                        width: 100% !important;
                        margin: 0 !important;
                        padding: 0 10mm !important; 
                        box-shadow: none !important;
                        transform: none !important;
                    }
                    .slip-grid-dynamic {
                        display: flex !important;
                        flex-wrap: wrap !important;
                        align-content: flex-start !important;
                        gap: 10mm !important;
                        width: 100% !important;
                    }
                    .voter-card-print {
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                    }
                    .no-print {
                        display: none !important;
                    }
                    * {
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                }

                /* --- LOADER --- */
                .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 40vh; width: 100%; }
                .spinner { width: 40px; height: 40px; border: 4px solid #E5E7EB; border-top: 4px solid #4338CA; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 12px; }
                .loader-text { color: #6B7280; font-weight: 600; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

                @media (max-width: 768px) {
                    .controls-row .d-flex {align-items: stretch !important; }
                    .filter-field { max-width: 100% !important; margin-top: 0; }
                    .btn-modern { width: 100%; }
                    .btn-export-modern.ms-auto { margin-left: 0 !important; }
                }
            `}</style>
        </div>
    );
};

export default BulkSlipPrintApp;